import { usePageRefresh } from '../../../context/PageRefreshContext';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as XLSX from 'xlsx';
import axiosInstance from '../../../axios/axiosInstance';
import apiEndpoints from '../../../axios/api';
import Pagination from '../../../components/common/Pagination';
import { selectedSocietyStorer } from '../../../utils/webStorer';
import { localDateString } from '../../../utils/helpers/localDate';
import { SOCIETY_PORTAL, SOCIETY_PORTAL_EMPLOYEES } from '../../../Routing/routes';

// Admin view of what employees did. The server merges four sources into one
// timeline: actions in the app, logins, attendance punches and the work
// entries employees write when punching.
const SOURCES = [
    { key: 'ALL', label: 'All', icon: 'mdi:format-list-bulleted' },
    { key: 'ACTION', label: 'Actions', icon: 'mdi:gesture-tap' },
    { key: 'WORK', label: 'Work Entries', icon: 'mdi:clipboard-text-outline' },
    { key: 'PUNCH', label: 'Punch In / Out', icon: 'mdi:fingerprint' },
    { key: 'LOGIN', label: 'Logins', icon: 'mdi:login-variant' },
];

const SOURCE_STYLE = {
    ACTION: { icon: 'mdi:gesture-tap', dot: 'tw-bg-blue-500', chip: 'tw-bg-blue-50 tw-text-blue-700' },
    WORK: { icon: 'mdi:clipboard-text-outline', dot: 'tw-bg-emerald-500', chip: 'tw-bg-emerald-50 tw-text-emerald-700' },
    PUNCH: { icon: 'mdi:fingerprint', dot: 'tw-bg-amber-500', chip: 'tw-bg-amber-50 tw-text-amber-700' },
    LOGIN: { icon: 'mdi:login-variant', dot: 'tw-bg-purple-500', chip: 'tw-bg-purple-50 tw-text-purple-700' },
};

const ACTION_LABELS = {
    CREATE: 'Created', UPDATE: 'Updated', DELETE: 'Deleted', VIEW: 'Viewed', EXPORT: 'Exported',
    IMPORT: 'Imported', APPROVE: 'Approved', REJECT: 'Rejected', OTHER: 'Activity',
    LOGIN: 'Logged in', LOGOUT: 'Logged out', PUNCH_IN: 'Punched in', PUNCH_OUT: 'Punched out',
    WORK_LOG: 'Work entry',
};

const titleCase = (v) => String(v || '')
    .replace(/[_-]+/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, c => c.toUpperCase());

const eventTitle = (row) => {
    const verb = ACTION_LABELS[row.action] || titleCase(row.action);
    if (row.source === 'ACTION') return `${verb}${row.module ? ` · ${titleCase(row.module)}` : ''}`;
    if (row.source === 'WORK') return row.module || 'Work entry';
    return verb;
};

const fmtTime = (v) => (v ? new Date(v).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '');
const fmtDay = (v) => (v ? new Date(v).toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' }) : '');
const fmtDateTime = (v) => (v ? new Date(v).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '');

const daysAgo = (n) => {
    const d = new Date();
    d.setDate(d.getDate() - n);
    return localDateString(d);
};

const LIMIT = 50;

const EmployeeWorkLog = () => {
    const navigate = useNavigate();
    const societyId = selectedSocietyStorer.getSocietyId();
    const [urlParams] = useSearchParams();

    const [employees, setEmployees] = useState([]);
    const [employeeId, setEmployeeId] = useState(urlParams.get('employeeId') || '');
    const [source, setSource] = useState('ALL');
    const [fromDate, setFromDate] = useState(daysAgo(6));
    const [toDate, setToDate] = useState(localDateString());
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [page, setPage] = useState(1);

    const [rows, setRows] = useState([]);
    const [meta, setMeta] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [exporting, setExporting] = useState(false);

    useEffect(() => {
        if (!societyId) return;
        axiosInstance.get(apiEndpoints.SOCIETY_GET_EMPLOYEES_DROPDOWN_API, { params: { society_id: societyId } })
            .then(res => setEmployees(Array.isArray(res?.data) ? res.data : []))
            .catch(() => setEmployees([]));
    }, [societyId]);

    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
        return () => clearTimeout(t);
    }, [search]);

    // Any filter change goes back to page 1.
    useEffect(() => { setPage(1); }, [employeeId, source, fromDate, toDate, debouncedSearch]);

    const buildParams = useCallback((extra = {}) => {
        const params = { society_id: societyId, page, limit: LIMIT, ...extra };
        if (employeeId) params.employee_id = employeeId;
        if (source !== 'ALL') params.source = source;
        if (fromDate) params.from_date = fromDate;
        if (toDate) params.to_date = toDate;
        if (debouncedSearch) params.search = debouncedSearch;
        return params;
    }, [societyId, page, employeeId, source, fromDate, toDate, debouncedSearch]);

    const invalidRange = Boolean(fromDate && toDate && toDate < fromDate);

    const fetchLog = useCallback(async () => {
        if (!societyId || invalidRange) return;
        setLoading(true);
        setError('');
        try {
            const res = await axiosInstance.get(apiEndpoints.SOCIETY_GET_EMPLOYEE_WORK_LOG_API, { params: buildParams() });
            setRows(Array.isArray(res?.data) ? res.data : []);
            setMeta(res?.meta?.pagination || null);
        } catch (err) {
            setRows([]);
            setMeta(null);
            setError(err?.response?.data?.message || err?.message || 'Failed to load work log');
        } finally {
            setLoading(false);
        }
    }, [societyId, invalidRange, buildParams]);

    useEffect(() => { fetchLog(); }, [fetchLog]);
    usePageRefresh(fetchLog);

    // Timeline grouped by calendar day (rows arrive newest first).
    const groups = useMemo(() => {
        const out = [];
        rows.forEach(r => {
            const day = fmtDay(r.occurred_at);
            const last = out[out.length - 1];
            if (last && last.day === day) last.items.push(r);
            else out.push({ day, items: [r] });
        });
        return out;
    }, [rows]);

    const counts = meta?.counts || {};
    const totalPages = meta?.totalPages || 1;

    const resetFilters = () => {
        setEmployeeId('');
        setSource('ALL');
        setFromDate(daysAgo(6));
        setToDate(localDateString());
        setSearch('');
    };

    // Excel of everything matching the filters (up to 500 rows per request,
    // fetched page by page).
    const handleExcel = async () => {
        if (exporting) return;
        setExporting(true);
        try {
            const all = [];
            for (let p = 1; p <= 40; p += 1) {
                const res = await axiosInstance.get(apiEndpoints.SOCIETY_GET_EMPLOYEE_WORK_LOG_API, { params: buildParams({ page: p, limit: 500 }) });
                const batch = Array.isArray(res?.data) ? res.data : [];
                all.push(...batch);
                if (p >= (res?.meta?.pagination?.totalPages || 1)) break;
            }
            const sourceLabel = Object.fromEntries(SOURCES.map(s => [s.key, s.label]));
            const wsData = [
                ['Employee Work Log'],
                [`Period: ${fromDate || '—'} to ${toDate || '—'}`],
                [],
                ['Date & Time', 'Employee', 'Type', 'Activity', 'Details', 'Status'],
                ...all.map(r => [
                    fmtDateTime(r.occurred_at),
                    r.employee_name,
                    sourceLabel[r.source] || r.source,
                    eventTitle(r),
                    r.detail || '',
                    r.status ? titleCase(r.status) : '',
                ]),
            ];
            const ws = XLSX.utils.aoa_to_sheet(wsData);
            ws['!cols'] = [22, 24, 14, 28, 60, 14].map(w => ({ wch: w }));
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Work Log');
            XLSX.writeFile(wb, `Employee_Work_Log_${fromDate || ''}_${toDate || ''}.xlsx`);
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || 'Export failed');
        } finally {
            setExporting(false);
        }
    };

    return (
        <div className="tw-p-5 tw-bg-gradient-to-br tw-from-gray-50 tw-to-slate-50 tw-min-h-screen">
            {/* Header */}
            <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3 tw-mb-4">
                <div className="tw-flex tw-items-center tw-gap-3">
                    <button
                        onClick={() => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_EMPLOYEES)}
                        className="tw-p-2 tw-rounded-md tw-bg-white tw-border tw-border-gray-200 hover:tw-bg-gray-50"
                    >
                        <Icon icon="mdi:arrow-left" className="tw-text-gray-600" />
                    </button>
                    <div>
                        <h1 className="tw-text-base tw-font-bold tw-text-gray-800">Employee Work Log</h1>
                        <p className="tw-text-[11px] tw-text-gray-500 tw-mt-0.5">
                            Actions, work entries, punches and logins of employees
                        </p>
                    </div>
                </div>
                <button
                    onClick={handleExcel}
                    disabled={exporting || loading || !rows.length}
                    className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-rounded-md tw-text-xs tw-font-medium tw-text-emerald-700 tw-border tw-border-emerald-200 tw-bg-emerald-50 hover:tw-bg-emerald-100 disabled:tw-opacity-50"
                >
                    <Icon icon={exporting ? 'mdi:loading' : 'mdi:file-excel-outline'} className={`tw-text-base ${exporting ? 'mdi-spin' : ''}`} />
                    Excel
                </button>
            </div>

            {/* Filters */}
            <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-p-4 tw-mb-4">
                <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-5 tw-gap-3">
                    <div className="lg:tw-col-span-2">
                        <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-600 tw-mb-1">Employee</label>
                        <select
                            value={employeeId}
                            onChange={e => setEmployeeId(e.target.value)}
                            className="tw-w-full tw-border tw-border-gray-200 tw-rounded-md tw-px-3 tw-py-2 tw-text-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-100"
                        >
                            <option value="">All employees</option>
                            {employees.map(e => (
                                <option key={e.employee_id} value={e.employee_id}>
                                    {[e.first_name, e.last_name].filter(Boolean).join(' ') || e.username || `Employee #${e.employee_id}`}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-600 tw-mb-1">From</label>
                        <input
                            type="date"
                            value={fromDate}
                            max={toDate || undefined}
                            onChange={e => setFromDate(e.target.value)}
                            className="tw-w-full tw-border tw-border-gray-200 tw-rounded-md tw-px-3 tw-py-2 tw-text-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-100"
                        />
                    </div>
                    <div>
                        <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-600 tw-mb-1">To</label>
                        <input
                            type="date"
                            value={toDate}
                            min={fromDate || undefined}
                            onChange={e => setToDate(e.target.value)}
                            className="tw-w-full tw-border tw-border-gray-200 tw-rounded-md tw-px-3 tw-py-2 tw-text-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-100"
                        />
                    </div>
                    <div>
                        <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-600 tw-mb-1">Search</label>
                        <input
                            type="text"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            placeholder="Activity or details"
                            className="tw-w-full tw-border tw-border-gray-200 tw-rounded-md tw-px-3 tw-py-2 tw-text-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-100"
                        />
                    </div>
                </div>
                {invalidRange && <p className="tw-text-[11px] tw-text-rose-600 tw-mt-2">"To" date cannot be before "From" date.</p>}

                <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-mt-3">
                    {SOURCES.map(s => (
                        <button
                            key={s.key}
                            type="button"
                            onClick={() => setSource(s.key)}
                            className={`tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1 tw-rounded-full tw-text-xs tw-font-medium tw-border tw-transition-colors ${source === s.key
                                ? 'tw-bg-blue-600 tw-border-blue-600 tw-text-white'
                                : 'tw-bg-white tw-border-gray-200 tw-text-gray-600 hover:tw-bg-gray-50'}`}
                        >
                            <Icon icon={s.icon} className="tw-text-sm" />
                            {s.label} ({counts[s.key] ?? 0})
                        </button>
                    ))}
                    <button
                        type="button"
                        onClick={resetFilters}
                        className="tw-ml-auto tw-text-xs tw-text-gray-500 hover:tw-text-gray-700 tw-underline"
                    >
                        Reset filters
                    </button>
                </div>
            </div>

            {/* Timeline */}
            <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm">
                {loading && (
                    <div className="tw-flex tw-justify-center tw-py-12">
                        <div className="tw-animate-spin tw-rounded-full tw-h-8 tw-w-8 tw-border-b-2 tw-border-blue-600"></div>
                    </div>
                )}
                {!loading && error && (
                    <p className="tw-m-4 tw-text-sm tw-text-rose-700 tw-bg-rose-50 tw-border tw-border-rose-100 tw-rounded-lg tw-p-3">{error}</p>
                )}
                {!loading && !error && rows.length === 0 && (
                    <div className="tw-text-center tw-py-12">
                        <Icon icon="mdi:timeline-clock-outline" className="tw-text-4xl tw-text-gray-300" />
                        <p className="tw-text-gray-500 tw-mt-2 tw-text-sm">No activity for these filters.</p>
                        <p className="tw-text-gray-400 tw-mt-1 tw-text-[11px]">
                            Only employees with a login account record actions and logins.
                        </p>
                    </div>
                )}
                {!loading && !error && groups.map(g => (
                    <div key={g.day} className="tw-border-b tw-border-gray-100 last:tw-border-b-0">
                        <div className="tw-px-4 tw-py-2 tw-bg-gray-50 tw-text-[11px] tw-font-semibold tw-text-gray-600 tw-uppercase tw-tracking-wide">
                            {g.day}
                        </div>
                        <ul>
                            {g.items.map((r, i) => {
                                const style = SOURCE_STYLE[r.source] || SOURCE_STYLE.ACTION;
                                return (
                                    <li key={`${r.source}-${r.occurred_at}-${i}`} className="tw-flex tw-gap-3 tw-px-4 tw-py-2.5 hover:tw-bg-gray-50">
                                        <span className="tw-w-14 tw-flex-shrink-0 tw-text-[11px] tw-text-gray-500 tw-pt-0.5">{fmtTime(r.occurred_at)}</span>
                                        <span className={`tw-w-7 tw-h-7 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 ${style.chip}`}>
                                            <Icon icon={style.icon} className="tw-text-sm" />
                                        </span>
                                        <div className="tw-min-w-0 tw-flex-1">
                                            <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-0.5">
                                                <span className="tw-text-sm tw-font-medium tw-text-gray-800">{eventTitle(r)}</span>
                                                {r.status && (
                                                    <span className="tw-text-[10px] tw-font-medium tw-px-1.5 tw-py-0.5 tw-rounded-full tw-bg-gray-100 tw-text-gray-600">
                                                        {titleCase(r.status)}
                                                    </span>
                                                )}
                                                {!employeeId && (
                                                    <span className="tw-text-[11px] tw-text-gray-500">· {r.employee_name}</span>
                                                )}
                                            </div>
                                            {r.detail && <p className="tw-text-xs tw-text-gray-600 tw-mt-0.5 tw-break-words">{r.detail}</p>}
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                ))}

                {!loading && rows.length > 0 && totalPages > 1 && (
                    <div className="tw-p-4 tw-border-t tw-border-gray-100">
                        <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
                    </div>
                )}
            </div>
        </div>
    );
};

export default EmployeeWorkLog;
